import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography, FormControlLabel, Checkbox } from '@mui/material';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../../components/controlledComponents/ControlledDateField.component';
import {
  TVehicle,
  TViewType,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import {
  useRoutesAndScheduleState,
  useRoutesAndScheduleActions,
} from '../../../contexts/routesAndSchedule.context';
import { TTransactionCurrency } from 'src/apis/finance/finance.interfaces';
import {
  isIsoDateRangeOrdered,
  zDateStringFromInput,
  zNumberFromInput,
} from 'src/utils/zodFormHelpers';

type VehicleFormData = {
  vehicleNumber: string;
  vehicleType: 'BUS' | 'VAN' | 'CAR';
  capacity: number;
  driverId: string;
  routeId: string;
  isActive: boolean;
  lastMaintenanceDate: string;
  nextMaintenanceDate: string;
  fuelType: 'PETROL' | 'DIESEL' | 'ELECTRIC' | 'HYBRID';
  registrationNumber: string;
  insuranceExpiryDate: string;
  permitExpiryDate: string;
  pricePerKilometer: number;
  priceCurrency: TTransactionCurrency;
};

const getVehicleFormSchema = (
  translateText: (key: string) => string
): ZodType<VehicleFormData> =>
  z.object({
    vehicleNumber: z.string().min(1, translateText('common.fieldRequired')),
    vehicleType: z.enum(['BUS', 'VAN', 'CAR'], {
      required_error: translateText('common.fieldRequired'),
    }),
    capacity: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    driverId: z.string(),
    routeId: z.string(),
    isActive: z.boolean(),
    lastMaintenanceDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    nextMaintenanceDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    fuelType: z.enum(['PETROL', 'DIESEL', 'ELECTRIC', 'HYBRID'], {
      required_error: translateText('common.fieldRequired'),
    }),
    registrationNumber: z
      .string()
      .min(1, translateText('common.fieldRequired')),
    insuranceExpiryDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    permitExpiryDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    pricePerKilometer: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    priceCurrency: z
      .string()
      .refine(
        (val): val is TTransactionCurrency =>
          Object.values(TTransactionCurrency).includes(val as TTransactionCurrency),
        {
          message: translateText('common.fieldRequired'),
        }
      )
      .transform((val) => val as TTransactionCurrency),
  })
    .refine(
      (data) =>
        isIsoDateRangeOrdered(
          data.lastMaintenanceDate,
          data.nextMaintenanceDate
        ),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextMaintenanceDate'],
      }
    ) as ZodType<VehicleFormData>;

const getVehicleTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'BUS',
      value:
        translateText('administration.routesAndSchedule.vehicleTypes.bus') ||
        'Bus',
    },
    {
      key: 'VAN',
      value:
        translateText('administration.routesAndSchedule.vehicleTypes.van') ||
        'Van',
    },
    {
      key: 'CAR',
      value:
        translateText('administration.routesAndSchedule.vehicleTypes.car') ||
        'Car',
    },
  ];

const getFuelTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'PETROL',
      value:
        translateText('administration.routesAndSchedule.fuelTypes.petrol') ||
        'Petrol',
    },
    {
      key: 'DIESEL',
      value:
        translateText('administration.routesAndSchedule.fuelTypes.diesel') ||
        'Diesel',
    },
    {
      key: 'ELECTRIC',
      value:
        translateText('administration.routesAndSchedule.fuelTypes.electric') ||
        'Electric',
    },
    {
      key: 'HYBRID',
      value:
        translateText('administration.routesAndSchedule.fuelTypes.hybrid') ||
        'Hybrid',
    },
  ];

const AddEditVehicleDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const vehicleFormSchema = getVehicleFormSchema(translateText);
  const { selectedVehicle, editMode, viewType, drivers, routes } =
    useRoutesAndScheduleState();
  const { setEditMode, setViewType, addVehicle, updateVehicle } =
    useRoutesAndScheduleActions();

  const isEditMode = editMode === 'edit' && viewType === TViewType.VEHICLES;
  const isCreateMode = editMode === 'create' && viewType === TViewType.VEHICLES;
  const isOpen =
    (isEditMode || isCreateMode) && viewType === TViewType.VEHICLES;

  const defaultValues: VehicleFormData = {
    vehicleNumber: '',
    vehicleType: 'BUS',
    capacity: 0,
    driverId: '',
    routeId: '',
    isActive: true,
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    fuelType: 'PETROL',
    registrationNumber: '',
    insuranceExpiryDate: '',
    permitExpiryDate: '',
    pricePerKilometer: 0,
    priceCurrency: TTransactionCurrency.INR,
  };

  const methods = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && selectedVehicle) {
        reset({
          vehicleNumber: selectedVehicle.vehicleNumber || '',
          vehicleType: selectedVehicle.vehicleType || 'BUS',
          capacity: selectedVehicle.capacity || 0,
          driverId: selectedVehicle.driverId || '',
          routeId: selectedVehicle.routeId || '',
          isActive: selectedVehicle.isActive ?? true,
          lastMaintenanceDate: selectedVehicle.lastMaintenanceDate
            ? new Date(selectedVehicle.lastMaintenanceDate)
              .toISOString()
              .split('T')[0]
            : '',
          nextMaintenanceDate: selectedVehicle.nextMaintenanceDate
            ? new Date(selectedVehicle.nextMaintenanceDate)
              .toISOString()
              .split('T')[0]
            : '',
          fuelType: selectedVehicle.fuelType || 'PETROL',
          registrationNumber: selectedVehicle.registrationNumber || '',
          insuranceExpiryDate: selectedVehicle.insuranceExpiryDate
            ? new Date(selectedVehicle.insuranceExpiryDate)
              .toISOString()
              .split('T')[0]
            : '',
          permitExpiryDate: selectedVehicle.permitExpiryDate
            ? new Date(selectedVehicle.permitExpiryDate)
              .toISOString()
              .split('T')[0]
            : '',
          pricePerKilometer: selectedVehicle.pricePerKilometer || 0,
          priceCurrency: selectedVehicle.priceCurrency || TTransactionCurrency.INR,
        });
      } else if (isCreateMode) {
        reset(defaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle, isEditMode, isCreateMode, isOpen, reset]);

  const onSubmitForm = (data: VehicleFormData) => {
    const vehicleData: TVehicle = {
      id: selectedVehicle?.id || '',
      vehicleNumber: data.vehicleNumber,
      vehicleType: data.vehicleType,
      capacity: data.capacity,
      driverId: data.driverId || '',
      routeId: data.routeId || '',
      isActive: data.isActive,
      lastMaintenanceDate: new Date(data.lastMaintenanceDate),
      nextMaintenanceDate: new Date(data.nextMaintenanceDate),
      fuelType: data.fuelType,
      registrationNumber: data.registrationNumber,
      insuranceExpiryDate: new Date(data.insuranceExpiryDate),
      permitExpiryDate: new Date(data.permitExpiryDate),
      pricePerKilometer: data.pricePerKilometer,
      priceCurrency: data.priceCurrency,
      vehicleBrand: '',
      vehicleModel: '',
      vehicleYear: 0,
      vehicleColor: '',
      vehicleEngine: '',
      vehicleTransmission: '',
      vehicleFuel: '',
      vehicleMileage: 0,
      vehicleImage: [],
    };

    if (isEditMode) {
      updateVehicle(vehicleData);
    } else {
      addVehicle(vehicleData);
    }
    setEditMode(undefined);
    setViewType(undefined);
  };

  const handleClose = () => {
    setEditMode(undefined);
    setViewType(undefined);
  };

  const driverOptions: TKeyValue<string>[] = drivers.map((driver) => ({
    key: driver.id,
    value: driver.name,
  }));

  const routeOptions: TKeyValue<string>[] = routes.map((route) => ({
    key: route.id,
    value: route.routeName,
  }));

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="add-edit-vehicle-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText('administration.routesAndSchedule.editVehicle') ||
            'Edit Vehicle'
            : translateText('administration.routesAndSchedule.addVehicle') ||
            'Add Vehicle'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={false}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="vehicle-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="vehicle-form-container"
            >
              <Grid item xs={12} data-testid="vehicle-basic-section">
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.routesAndSchedule.details.basicInfo'
                  ) || 'Basic Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-number-field">
                <ControlledTextField
                  control={control}
                  name="vehicleNumber"
                  id="vehicle-number"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.vehicleNumber'
                    ) || 'Vehicle Number'
                  }
                  errorMessage={errors.vehicleNumber?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-type-field">
                <ControlledSelectField
                  control={control}
                  name="vehicleType"
                  id="vehicle-type"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.type'
                    ) || 'Vehicle Type'
                  }
                  itemList={getVehicleTypeOptions(translateText)}
                  errorMessage={errors.vehicleType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-capacity-field">
                <ControlledTextField
                  control={control}
                  name="capacity"
                  id="vehicle-capacity"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.capacity'
                    ) || 'Capacity'
                  }
                  errorMessage={errors.capacity?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="vehicle-registration-field"
              >
                <ControlledTextField
                  control={control}
                  name="registrationNumber"
                  id="vehicle-registration"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.registrationNumber'
                    ) || 'Registration Number'
                  }
                  errorMessage={errors.registrationNumber?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-fuel-type-field">
                <ControlledSelectField
                  control={control}
                  name="fuelType"
                  id="vehicle-fuel-type"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.fuelType'
                    ) || 'Fuel Type'
                  }
                  itemList={getFuelTypeOptions(translateText)}
                  errorMessage={errors.fuelType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-status-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isActive')}
                      onChange={(e) => {
                        methods.setValue('isActive', e.target.checked);
                      }}
                    />
                  }
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.status'
                    ) || 'Active'
                  }
                />
              </Grid>

              <Grid item xs={12} data-testid="vehicle-assignments-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.assignments'
                  ) || 'Assignments'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-route-field">
                <ControlledSelectField
                  control={control}
                  name="routeId"
                  id="vehicle-route"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.route'
                    ) || 'Route'
                  }
                  itemList={routeOptions}
                  errorMessage={errors.routeId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-driver-field">
                <ControlledSelectField
                  control={control}
                  name="driverId"
                  id="vehicle-driver"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.driver'
                    ) || 'Driver'
                  }
                  itemList={driverOptions}
                  errorMessage={errors.driverId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="vehicle-maintenance-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.maintenance'
                  ) || 'Maintenance'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="vehicle-last-maintenance-field"
              >
                <ControlledDateField
                  control={control}
                  name="lastMaintenanceDate"
                  id="vehicle-last-maintenance"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.lastMaintenance'
                    ) || 'Last Maintenance Date'
                  }
                  errorMessage={errors.lastMaintenanceDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="vehicle-next-maintenance-field"
              >
                <ControlledDateField
                  control={control}
                  name="nextMaintenanceDate"
                  id="vehicle-next-maintenance"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.nextMaintenance'
                    ) || 'Next Maintenance Date'
                  }
                  errorMessage={errors.nextMaintenanceDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="vehicle-legal-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.legal'
                  ) || 'Legal & Compliance'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-insurance-field">
                <ControlledDateField
                  control={control}
                  name="insuranceExpiryDate"
                  id="vehicle-insurance"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.insuranceExpiry'
                    ) || 'Insurance Expiry Date'
                  }
                  errorMessage={errors.insuranceExpiryDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="vehicle-permit-field">
                <ControlledDateField
                  control={control}
                  name="permitExpiryDate"
                  id="vehicle-permit"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.permitExpiry'
                    ) || 'Permit Expiry Date'
                  }
                  errorMessage={errors.permitExpiryDate?.message}
                  isGridItem={false}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditVehicleDetailsView;
